import { ReactNode, useState } from 'react';
import { Icons } from './Icons';
import type { IconComponent } from '../types';

interface Badge {
  text: string;
  color?: 'green' | 'red' | 'blue' | 'orange' | 'purple';
}

interface Action {
  label: string;
  onClick: () => void;
  active?: boolean;
  icon?: IconComponent;
}

interface CollapsibleSectionProps {
  icon: IconComponent;
  title: string;
  badge?: Badge;
  actions?: Action[];
  children: ReactNode;
  defaultOpen?: boolean;
  miniPreview?: ReactNode; // What to show when collapsed
  onToggle?: (isOpen: boolean) => void;
}

/**
 * Collapsible Section Component with Mini-Preview
 *
 * Features:
 * - Expandable/collapsible section with smooth animation
 * - Icon + Title + Optional badge
 * - Actions buttons in header
 * - Mini-preview when collapsed
 * - Callback on toggle
 */
export function CollapsibleSection({
  icon: Icon,
  title,
  badge,
  actions = [],
  children,
  defaultOpen = true,
  miniPreview,
  onToggle
}: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const handleToggle = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    onToggle?.(newState);
  };

  const getBadgeColor = (color?: string) => {
    switch (color) {
      case 'green':
        return { bg: '#10b981', text: 'white' };
      case 'red':
        return { bg: '#ef4444', text: 'white' };
      case 'blue':
        return { bg: '#3b82f6', text: 'white' };
      case 'orange':
        return { bg: '#f59e0b', text: 'white' };
      case 'purple':
        return { bg: '#8b5cf6', text: 'white' };
      default:
        return { bg: 'var(--bg)', text: 'var(--text)' };
    }
  };

  if (!isOpen && miniPreview) {
    // Collapsed with preview
    return (
      <div
        className="card"
        style={{
          marginBottom: '20px',
          padding: '12px 16px',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          border: '1px solid var(--border)'
        }}
        onClick={handleToggle}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
            <Icon size={18} style={{ color: 'var(--muted)' }} />
            <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--muted)' }}>
              {title}
            </span>
            {badge && (
              <span className="badge-animate" style={{
                padding: '2px 8px',
                background: getBadgeColor(badge.color).bg,
                color: getBadgeColor(badge.color).text,
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 600
              }}>
                {badge.text}
              </span>
            )}
          </div>

          {/* Mini preview content */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {miniPreview}
            <Icons.ChevronDown size={18} style={{ color: 'var(--muted)' }} />
          </div>
        </div>
      </div>
    );
  }

  // Expanded view
  return (
    <div className="card" style={{ marginBottom: '20px' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        {/* Left side: Icon + Title + Badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Icon size={20} />
          <h2 style={{ fontSize: '18px', fontWeight: 600, margin: 0 }}>
            {title}
          </h2>
          {badge && (
            <span className="badge-animate" style={{
              padding: '4px 10px',
              background: getBadgeColor(badge.color).bg,
              color: getBadgeColor(badge.color).text,
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 600
            }}>
              {badge.text}
            </span>
          )}
        </div>

        {/* Right side: Actions + Collapse button */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          {actions.map((action, index) => (
            <button
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                action.onClick();
              }}
              className={action.active ? 'btn-primary' : 'btn-ghost'}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              {action.icon && <action.icon size={14} />}
              {action.label}
            </button>
          ))}
          <button
            onClick={handleToggle}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              color: 'var(--muted)',
              display: 'flex',
              alignItems: 'center'
            }}
            title="Masquer"
          >
            <Icons.ChevronUp size={18} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="animate-slide-down" style={{
        animation: 'slideDown 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
      }}>
        {children}
      </div>
    </div>
  );
}
